import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useI18n } from '@/i18n/context'
import { LANGUAGE_OPTIONS } from '@/constants/i18n'
import { apiFetch } from '@/lib/api-client'
import { Flag } from '@/components/brand/flags'
import { LogoBadge, Wordmark } from '@/components/brand/logo'
import { cn } from '@/lib/utils'

interface AuthShellProps {
    children: ReactNode
    showLanguages?: boolean
    wide?: boolean
}

export const LanguageSwitch: FC<{ className?: string }> = ({ className }): ReactNode => {
    const { locale, m } = useI18n()
    const router = useRouter()
    const [busy, setBusy] = useState(false)

    const change = async (code: string) => {
        if (code === locale || busy) return

        setBusy(true)
        await apiFetch('/api/locale', { locale: code })
        await router.replace(router.asPath, undefined, { scroll: false })
        setBusy(false)
    }

    return (
        <div className={cn('flex items-center gap-1 rounded-full border border-gold/20 bg-black/30 p-1 backdrop-blur', className)} role='group' aria-label={m.auth.languageLabel}>
            {LANGUAGE_OPTIONS.map(option => (
                <button
                    key={option.code}
                    type='button'
                    onClick={() => change(option.code)}
                    aria-pressed={option.code === locale}
                    aria-label={option.label}
                    title={option.label}
                    className={cn(
                        'flex h-8 items-center gap-1.5 rounded-full px-2.5 text-xs font-semibold uppercase transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold',
                        option.code === locale ? 'bg-gold/20 text-gold-bright' : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    <Flag code={option.flag} className='w-4.5' />
                    <span className='hidden sm:inline'>{option.code}</span>
                </button>
            ))}
        </div>
    )
}

export const AuthShell: FC<AuthShellProps> = ({ children, showLanguages = true, wide = false }): ReactNode => {
    const { m } = useI18n()

    return (
        <div className='relative min-h-screen overflow-hidden sv-luxe-bg'>
            <div className='pointer-events-none absolute inset-0' aria-hidden='true'>
                <Image
                    src='/images/hero-villa.jpg'
                    alt=''
                    fill
                    priority
                    sizes='100vw'
                    className='object-cover opacity-[0.16]'
                />
                <div className='absolute inset-0 bg-gradient-to-b from-[#0a1310]/70 via-[#0a1310]/88 to-[#0a1310]' />
                <div className='absolute -top-40 start-1/2 h-[520px] w-[820px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(212,175,90,0.22),transparent)] rtl:translate-x-1/2' />
            </div>

            <header className='relative z-10 mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-5 py-5 sm:px-8'>
                <Link href='/auth' className='flex items-center gap-3 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold'>
                    <LogoBadge size={44} priority />
                    <Wordmark className='text-sm' />
                </Link>

                {showLanguages && <LanguageSwitch />}
            </header>

            <main className={cn('relative z-10 mx-auto w-full px-4 pb-16 pt-4 sm:px-6', wide ? 'max-w-2xl' : 'max-w-xl')}>
                {children}
            </main>

            <footer className='relative z-10 pb-8 text-center text-xs text-muted-foreground'>
                <nav className='flex items-center justify-center gap-5'>
                    <Link href='/privacy' className='hover:text-gold'>{m.footer.privacy}</Link>
                    <Link href='/terms' className='hover:text-gold'>{m.footer.terms}</Link>
                </nav>

                <p className='mt-3 tracking-[0.25em] text-gold/70'>{m.footer.tagline}</p>
            </footer>
        </div>
    )
}

export const AuthCard: FC<{ children: ReactNode, className?: string }> = ({ children, className }): ReactNode => (
    <section className={cn('sv-glass rounded-[28px] border border-gold/20 p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.8)] sm:p-9', className)}>
        {children}
    </section>
)
